import React, { createContext, useContext, useEffect, ReactNode } from 'react'
import posthog from 'posthog-js'
import { getAnalyticsConsent } from '@/global/utils/cookieConsent'

interface PostHogContextType {
  posthog: typeof posthog | null
  isEnabled: boolean
}

const PostHogContext = createContext<PostHogContextType>({
  posthog: null,
  isEnabled: false,
})

export const usePostHogContext = () => useContext(PostHogContext)

interface PostHogProviderProps {
  children: ReactNode
}

export const PostHogProvider: React.FC<PostHogProviderProps> = ({ children }) => {
  const isEnabled = import.meta.env.VITE_ENABLE_ANALYTICS === 'true'
  const posthogKey = import.meta.env.VITE_POSTHOG_KEY
  const posthogHost = import.meta.env.VITE_POSTHOG_HOST

  useEffect(() => {
    // Only initialize PostHog if analytics are enabled AND user has consented
    const hasConsent = getAnalyticsConsent()
    
    if (isEnabled && hasConsent && posthogKey && posthogHost) {
      posthog.init(posthogKey, {
        api_host: posthogHost,
        person_profiles: 'identified_only', // Only create profiles for identified users
        capture_pageview: false, // We'll manually handle pageviews
        capture_pageleave: true,
        disable_session_recording: true, // No session recording (per requirements)
        autocapture: {
          dom_event_allowlist: ['click'], // Only capture clicks, not all interactions
          url_allowlist: [], // Don't autocapture by URL
        },
        loaded: (posthog) => {
          if (import.meta.env.VITE_APP_ENV === 'development') {
            console.log('[PostHog] Initialized successfully with user consent')
          }
        },
      })
    } else if (isEnabled && !hasConsent) {
      if (import.meta.env.VITE_APP_ENV === 'development') {
        console.log('[PostHog] Not initialized - awaiting user consent')
      }
    }
  }, [isEnabled, posthogKey, posthogHost])

  return (
    <PostHogContext.Provider value={{ posthog: isEnabled ? posthog : null, isEnabled }}>
      {children}
    </PostHogContext.Provider>
  )
}
